import { useState, useMemo, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Plus, Search, Sparkles, Building2, TrendingUp, Globe, X, Trash2, Loader2, ThumbsUp, MessageCircle, ArrowLeft, CheckCircle, Users } from "lucide-react";
import { toast } from "sonner";
import { useSelector } from "react-redux";
import { POST_CATEGORIES, getCategoryById } from "./PostCategoriesConfig";
import { PostCardSkeleton, isLikedByUser } from "./CommunityShared";
import PostCard from "./PostCard";
import PostDetailModal from "./PostDetails";
import FeedEntry from "./FeedEntry";
import CommunitySidebar, { WebstorePreview } from "./CommunitySidebar";
import ProfilePromptDialog from "./ProfilePromptDialog";
import WebStoreConfig from "../Settings/WebStoreConfig/WebStoreConfig";
import CompanyProfileModal from "./CompanyProfileModal";
import { getPostsAPI, deletePostAPI, getPostDetailsAPI, likePostAPI, reportPostAPI } from "./FeedAPI";
import { getCurrentUser, getUploadUrl, getCommunityRights, isWebstoreComplete, openInExternalBrowser } from "../../Utils/Utilities";
import { getAuthData } from "../../Utils/AuthUtils";
import apiService from "../../Services/APIService";
import { Button } from "../../Components/UI/button";
import { Input } from "../../Components/UI/input";
import { Switch } from "../../Components/UI/switch";
import { Drawer, DrawerContent } from "../../Components/UI/drawer";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter } from "../../Components/UI/alert-dialog";
import useIsMobile from "../../Hooks/useIsMobile";
import { SHOW_COMMUNITY } from "../../Utils/FeatureFlags";

const WHATSAPP_COMMUNITY_LINK = "https://api.whatsapp.com/send/?phone=917405810278&text=%E0%A4%87%E0%A4%A8%E0%A4%AA%E0%A5%81%E0%A4%9F%20%E0%A4%89%E0%A4%AA%E0%A4%95%E0%A4%B0%E0%A4%A3%20%E0%A4%86%E0%A4%AA%E0%A4%95%E0%A5%87%20%E0%A4%A6%E0%A5%8D%E0%A4%B5%E0%A4%BE%E0%A4%B0%E0%A4%BE%20%E0%A4%9A%E0%A5%81%E0%A4%A8%E0%A5%80%20%E0%A4%97%E0%A4%88%20%E0%A4%AD%E0%A4%BE%E0%A4%B7%E0%A4%BE%20%E0%A4%AE%E0%A5%87%E0%A4%82%20%E0%A4%B5%E0%A5%87%E0%A4%AC%20%E0%A4%B5%E0%A4%B0%20%E0%A4%95%E0%A4%B9%E0%A5%80%E0%A4%82%20%E0%A4%AD%E0%A5%88%20%E0%A4%B2%E0%A4%BF%E0%A4%96%E0%A4%A8%E0%A4%BE%20%E0%A4%86%E0%A4%B8%E0%A4%BE%E0%A4%A8%20%E0%A4%AC%E0%A4%A8%E0%A4%BE%E0%A4%A4%E0%A4%BE%20%E0%A4%B9%E0%A5%88%F0%9F%98%80%F0%9F%98%80&type=phone_number&app_absent=0";

// ─── Mobile "Coming Soon" Placeholder ────────────────────────────────────────
function CommunityComingSoonMobile() {
  const agencyData = useSelector((state) => state.agencyData?.data);
  const currentUser = getCurrentUser();
  const agencyID = Number(agencyData?.AgencyID || currentUser?.AgencyID || 0);
  const isAllowedAgency = [41344, 52].includes(agencyID);

  const timerRef = useRef(null);

  const startPress = () => {
    if (!isAllowedAgency) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      window.open(WHATSAPP_COMMUNITY_LINK, "_blank");
    }, 2000);
  };

  const endPress = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  return (
    <div
      className="min-h-[70vh] flex flex-col items-center justify-center text-center px-6 select-none cursor-pointer"
      onTouchStart={startPress}
      onTouchEnd={endPress}
      onTouchCancel={endPress}
      onTouchMove={endPress}
      onMouseDown={startPress}
      onMouseUp={endPress}
      onMouseLeave={endPress}
    >
      <div className="relative w-24 h-24 mb-5">
        <div className="absolute inset-0 rounded-full bg-sahi-green-light animate-pulse" />
        <div className="absolute inset-0 flex items-center justify-center">
          <Globe className="h-11 w-11 text-sahi-primary-color" />
        </div>
        <div className="absolute -top-1 -right-1 w-8 h-8 rounded-full bg-sahi-white border border-sahi-border-color shadow-sm flex items-center justify-center">
          <ThumbsUp className="h-3.5 w-3.5 text-sahi-green" />
        </div>
        <div className="absolute -bottom-1 -left-2 w-8 h-8 rounded-full bg-sahi-white border border-sahi-border-color shadow-sm flex items-center justify-center">
          <MessageCircle className="h-3.5 w-3.5 text-sahi-orange" />
        </div>
      </div>

      <h3 className="text-lg font-bold text-text-color">Network is Coming Soon</h3>
      <p className="text-sm text-sub-text-color mt-2 max-w-sm">
        We're building a space to connect, share updates, and discover business opportunities across the SahiClub B2B Network.
      </p>
    </div>
  );
}

// ─── Main Community Module Component ──────────────────────────────────────────
export default function Community() {
  const [posts, setPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(true);
  const [viewAll, setViewAll] = useState(false);
  const [activeCategory, setActiveCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [createPostOpen, setCreatePostOpen] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [focusCommentInput, setFocusCommentInput] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteAlertOpen, setDeleteAlertOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  useEffect(() => {
    const handleToggleFilterDrawer = () => {
      setFilterDrawerOpen(true);
    };
    window.addEventListener("community-toggle-filter-drawer", handleToggleFilterDrawer);
    return () => {
      window.removeEventListener("community-toggle-filter-drawer", handleToggleFilterDrawer);
    };
  }, []);

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlPostID = searchParams.get("postID") || searchParams.get("p");
  const openedUrlPostRef = useRef(null);

  const isMobile = useIsMobile();
  const authData = getAuthData();
  const rawCurrentUser = getCurrentUser() || {};
  // Jarvis (authData) carries AgencyName; the 'U' table row from getCurrentUser() doesn't.
  const currentUser = { ...rawCurrentUser, AgencyName: authData?.AgencyName || rawCurrentUser.AgencyName };

  const [previewAgency, setPreviewAgency] = useState(null);

  const handleCreatePostClick = () => {
    setCreatePostOpen(true);
  };

  const handleAgencyClick = (agencyID, postObj = null) => {
    if (!agencyID) return;
    if (isMobile) {
      if (postObj?.PostID) {
        try {
          sessionStorage.setItem("lastVisitedCommunityPostID", String(postObj.PostID));
        } catch (e) { }
      }
      navigate(`/store?m=${agencyID}&i=1`);
      return;
    }
    const targetAgency = {
      agencyID,
      agencyName: postObj?.CreatedAgencyName || "",
      agencyLogo: postObj?.CreatedAgencyLogo || "",
      city: postObj?.City || postObj?.AgencyCity || postObj?.CreatedCity || "",
      state: postObj?.State || postObj?.AgencyState || postObj?.CreatedState || "",
      isVerified: Boolean(postObj?.IsVerified),
      post: postObj
    };
    setPreviewAgency(targetAgency);
  };

  const normalizePost = (row) => {
    let rawPhoto = row.Photos || row.Image || "";
    if (typeof rawPhoto === "string") {
      const trimmed = rawPhoto.trim();
      if (trimmed === "[]" || trimmed === "{}" || trimmed === "null" || trimmed === "undefined") {
        rawPhoto = "";
      }
    }
    let photoPaths = [];
    if (typeof rawPhoto === "string" && rawPhoto.trim()) {
      if (rawPhoto.trim().startsWith("[")) {
        try {
          const parsed = JSON.parse(rawPhoto);
          if (Array.isArray(parsed)) {
            photoPaths = parsed.map(p => typeof p === 'string' ? p.trim() : String(p?.path || p?.url || '')).filter(Boolean);
          }
        } catch (e) {
          photoPaths = [rawPhoto.trim()];
        }
      } else {
        photoPaths = [rawPhoto.trim()];
      }
    } else if (Array.isArray(rawPhoto)) {
      photoPaths = rawPhoto.map(p => typeof p === 'string' ? p.trim() : String(p?.path || p?.url || '')).filter(Boolean);
    }

    photoPaths = photoPaths.filter(p => p && p !== "[]" && p !== "{}" && p !== "null" && p !== "undefined" && !p.endsWith("/Uploads/"));
    const photos = photoPaths.map(p => getUploadUrl(`Uploads/${p}`));
    const primaryPhoto = photos[0] || "";
    const primaryPhotoPath = photoPaths[0] || "";

    let rawComments = row.comments || row.Comments || [];
    if (typeof rawComments === "string") {
      try {
        rawComments = JSON.parse(rawComments);
      } catch (e) {
        rawComments = [];
      }
    }
    const comments = Array.isArray(rawComments)
      ? rawComments.map((c) => ({
        CID: Number(c.CID ?? c.cid ?? 0),
        PID: Number(c.PID ?? c.pid ?? c.ParentCommentID ?? c.parentCommentId ?? 0),
        UID: Number(c.UID ?? c.uid ?? 0),
        UName: c.UName || c.uname || "",
        AName: c.AName || c.aname || "",
        AgencyID: c.AgencyID || c.agencyID || c.AID || c.aid || c.CreatedAgencyID || c.CreatedAID,
        Val: c.Val || c.val || "",
        DT: c.DT || c.dt || "",
        IsD: Boolean(c.IsD || c.isD || c.IsDeleted || c.isDeleted)
      }))
      : [];

    return {
      ...row,
      Photo: primaryPhoto,
      PhotoPath: primaryPhotoPath,
      Photos: photos,
      PhotoPaths: photoPaths,
      CreatedAgencyLogo: row.CreatedAgencyLogo ? getUploadUrl(`Uploads/${row.CreatedAgencyLogo}`) : "",
      comments
    };
  };

  const loadPosts = async (targetViewAll = viewAll) => {
    setPostsLoading(true);
    try {
      const rows = await getPostsAPI(targetViewAll);
      const normalized = Array.isArray(rows) ? rows.map(normalizePost) : [];
      setPosts(normalized);
      return normalized;
    } catch (error) {
      console.error("Error loading community feed:", error);
      toast.error("Could not load the community feed.", {
        description: error?.message || "Please try again or contact support if the issue persists.",
        duration: 4000
      });
      setPosts([]);
      return [];
    } finally {
      setPostsLoading(false);
    }
  };

  const handleToggleViewAll = (checked) => {
    setViewAll(checked);
    loadPosts(checked);
  };

  useEffect(() => {
    if (isMobile && !SHOW_COMMUNITY) {
      setPostsLoading(false);
      return;
    }
    loadPosts().then((loadedPosts) => {
      if (urlPostID && openedUrlPostRef.current !== urlPostID) {
        openedUrlPostRef.current = urlPostID;
        const matched = loadedPosts.find((p) => String(p.PostID) === String(urlPostID));
        if (matched) {
          openPostDetail(matched);
        } else {
          openPostDetail({ PostID: Number(urlPostID) });
        }
      } else {
        try {
          const lastPostID = sessionStorage.getItem("lastVisitedCommunityPostID");
          if (lastPostID) {
            sessionStorage.removeItem("lastVisitedCommunityPostID");
            setTimeout(() => {
              const el = document.getElementById(`community-post-${lastPostID}`);
              if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "center" });
              }
            }, 150);
          }
        } catch (e) { }
      }
    });
  }, [isMobile]);

  // If urlPostID changes dynamically (e.g. right after posting from onboarding) while
  // Community is already mounted, the loaded feed may predate the new post - refresh a
  // few times so the full post opens, falling back to PostID only if it never appears.
  useEffect(() => {
    if (!urlPostID || openedUrlPostRef.current === urlPostID) return;
    openedUrlPostRef.current = urlPostID;

    let cancelled = false;
    const findPost = (list) => list.find((p) => String(p.PostID) === String(urlPostID));

    const openWhenReady = async (attempt = 0) => {
      const refreshed = await loadPosts();
      if (cancelled) return;
      const matched = findPost(refreshed);
      if (matched) {
        openPostDetail(matched);
      } else if (attempt < 4) {
        setTimeout(() => openWhenReady(attempt + 1), 700);
      } else {
        openPostDetail({ PostID: Number(urlPostID) });
      }
    };

    openWhenReady();
    return () => { cancelled = true; };
  }, [urlPostID]);

  useEffect(() => {
    const handleOpenPost = () => handleCreatePostClick();
    window.addEventListener("open-community-post", handleOpenPost);
    return () => window.removeEventListener("open-community-post", handleOpenPost);
  }, []);

  // Filter posts
  const filteredPosts = useMemo(() => {
    return posts.filter((post) => {
      const matchesCategory = activeCategory ? getCategoryById(post.Type).id === activeCategory : true;
      const matchesSearch = searchQuery.trim()
        ? post.Title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.Detail.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.CreatedAgencyName.toLowerCase().includes(searchQuery.toLowerCase())
        : true;
      return matchesCategory && matchesSearch;
    });
  }, [posts, activeCategory, searchQuery]);

  const handlePostCreated = () => {
    loadPosts();
  };

  const handleLikeToggle = async (post) => {
    // feed_Action's LIKE branch toggles server-side: removes the UserID from LikedUIDs if
    // already present, adds it otherwise. We apply optimistic updates immediately so UI responds instantly.
    const uid = String(currentUser?.UserID || "");
    const wasLiked = isLikedByUser(post, currentUser);

    const applyToggle = (p) => {
      if (p.PostID !== post.PostID) return p;
      const likedUIDs = (p.LikedUIDs || "").split(",").map((s) => s.trim()).filter(Boolean);
      const nextLikedUIDs = wasLiked ? likedUIDs.filter((id) => id !== uid) : [...likedUIDs, uid];
      return {
        ...p,
        CntLikes: wasLiked ? Math.max((p.CntLikes || 0) - 1, 0) : (p.CntLikes || 0) + 1,
        LikedUIDs: nextLikedUIDs.join(",")
      };
    };

    const revertToggle = (p) => {
      if (p.PostID !== post.PostID) return p;
      const likedUIDs = (p.LikedUIDs || "").split(",").map((s) => s.trim()).filter(Boolean);
      const prevLikedUIDs = wasLiked ? [...likedUIDs, uid] : likedUIDs.filter((id) => id !== uid);
      return {
        ...p,
        CntLikes: wasLiked ? (p.CntLikes || 0) + 1 : Math.max((p.CntLikes || 0) - 1, 0),
        LikedUIDs: prevLikedUIDs.join(",")
      };
    };

    // 1. Optimistic instant UI update
    setPosts((prev) => prev.map(applyToggle));
    setSelectedPost((prev) => (prev && prev.PostID === post.PostID ? applyToggle(prev) : prev));

    // 2. Perform background API call
    try {
      await likePostAPI(post.PostID);
    } catch (error) {
      console.error("Error liking post:", error);
      // Revert if API fails
      setPosts((prev) => prev.map(revertToggle));
      setSelectedPost((prev) => (prev && prev.PostID === post.PostID ? revertToggle(prev) : prev));
      toast.error(error?.message || "Could not update your like on this post.");
    }
  };

  const handleReportPost = async (post) => {
    try {
      await reportPostAPI(post.PostID);
      toast.info("Post reported.", { description: "Thank you for letting us know." });
    } catch (error) {
      console.error("Error reporting post:", error);
      toast.error(error?.message || "Could not report this post.");
    }
  };

  // The COMMENT branch of feed_Action returns the authoritative comments/counts (it re-runs
  // feed_GetPostDetails internally), so this just syncs local state to that server response.
  const handleAddReply = (postID, mappedComments, updatedCntViews, updatedCntComments) => {
    setPosts((prev) =>
      prev.map((p) => (p.PostID === postID ? {
        ...p,
        comments: mappedComments,
        CntViews: updatedCntViews ?? p.CntViews,
        CntComments: updatedCntComments ?? p.CntComments
      } : p))
    );
    if (selectedPost && selectedPost.PostID === postID) {
      setSelectedPost((prev) => ({
        ...prev,
        comments: mappedComments,
        CntViews: updatedCntViews ?? prev.CntViews,
        CntComments: updatedCntComments ?? prev.CntComments
      }));
    }
  };

  const requestDeletePost = (post) => {
    setDeleteTarget(post);
    setDeleteAlertOpen(true);
  };

  const handleDeletePost = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deletePostAPI(deleteTarget);
      setPosts((prev) => prev.filter((p) => p.PostID !== deleteTarget.PostID));
      toast.success("Post deleted.");
      setDetailOpen(false);
      setDeleteAlertOpen(false);
      setDeleteTarget(null);
    } catch (error) {
      console.error("Error deleting post:", error);
      toast.error("This post could not be deleted.", {
        description: error?.message || "Please try again or contact support if the issue persists.",
        duration: 4000
      });
    } finally {
      setDeleting(false);
    }
  };

  const openPostDetail = async (post, focusInput = false, imageIndex = 0) => {
    setFocusCommentInput(focusInput);
    setSelectedImageIndex(imageIndex || 0);
    setSelectedPost(post);
    setDetailOpen(true);
    setDetailLoading(true);
    try {
      // feed_GetPostDetails bumps the view count server-side and returns the up-to-date
      // counts/comments for this post - Title/Photos/agency info aren't part of that result
      // set, so those keep coming from the already-loaded feed list (merged with `post`).
      const { summary, comments } = await getPostDetailsAPI(post.PostID);
      const mappedComments = (comments || []).map((c) => ({
        CID: c.CID,
        PID: Number(c.PID || c.ParentCommentID || 0),
        UID: c.UID,
        UName: c.UName,
        AName: c.AName,
        AgencyID: c.AgencyID || c.AID || c.CreatedAgencyID || c.CreatedAID,
        Val: c.Val,
        DT: c.DT,
        IsD: Boolean(c.IsD || c.isD || c.IsDeleted || c.isDeleted)
      }));
      const updatedCntViews = summary?.CntViews ?? post.CntViews;
      const updatedCntComments = summary?.CntComments ?? post.CntComments;

      setSelectedPost((prev) => (prev?.PostID === post.PostID ? {
        ...prev,
        Title: summary?.PostTitle ?? prev.Title,
        CntViews: updatedCntViews,
        CntComments: updatedCntComments,
        comments: mappedComments
      } : prev));

      setPosts((prev) =>
        prev.map((p) => (p.PostID === post.PostID ? { ...p, CntViews: updatedCntViews, CntComments: updatedCntComments, comments: mappedComments } : p))
      );
    } catch (error) {
      console.error("Error loading post details:", error);
      toast.error("Could not load this post.", {
        description: error?.message || "Please try again or contact support if the issue persists.",
        duration: 4000
      });
    } finally {
      setDetailLoading(false);
    }
  };

  if (isMobile && !SHOW_COMMUNITY) {
    return <CommunityComingSoonMobile />;
  }

  return (
    <div className="h-full w-full flex flex-col bg-[var(--background)] space-y-3 sm:space-y-4 max-lg:overflow-y-auto lg:overflow-hidden">
      {/* Top Header Row - Desktop only */}
      <div className="hidden sm:flex items-center justify-between gap-4 pt-1 shrink-0">
        {/* Left-aligned Title */}
        <h1 className="text-3xl font-bold text-text-color web-header-name">
          Community
        </h1>

        {/* Right Action Area: Search Bar + Create Post Button */}
        <div className="flex items-center gap-3">
          <div className="relative w-64 md:w-80 sahi-radius filter-border">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-dropdown-white" />
            <Input
              placeholder="Search posts, businesses..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-10 border-none focus:outline-none focus:ring-0 shadow-none bg-sahi-white"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-sub-text-color hover:text-text-color cursor-pointer"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          <Button
            onClick={handleCreatePostClick}
            className="button-config text-sm sahi-primary-button items-center py-[6px] px-[12px] shrink-0"
          >
            <Plus className="h-4 w-4" />
            Create Post
          </Button>
        </div>
      </div>

      {/* Create a New Post prompt - Mobile only */}
      <button
        type="button"
        onClick={handleCreatePostClick}
        className="sm:hidden w-full bg-sahi-white border border-sahi-border-color sahi-radius px-4 py-3 text-left shadow-xs active:scale-[0.99] transition-transform"
      >
        <div className="text-xs text-sub-text-color">Grow your business and shine in the Community</div>
        <div className="text-base font-bold text-text-color mt-0.5">Promote your Business</div>
      </button>

      {/* Category Filter Tabs & Network Toggle - Desktop Only (Mobile uses Filter drawer) */}
      <div className="hidden sm:flex items-center justify-between gap-4 py-1 shrink-0">
        {/* Left: Category Filters & Network Toggle */}
        <div className="flex items-center gap-2.5 overflow-x-auto no-scrollbar">
          {/* All Posts Tab */}
          <button
            onClick={() => setActiveCategory(null)}
            title="All Posts"
            className={`shrink-0 flex items-center gap-2 rounded-[5px] text-xs lg:text-sm border transition-all duration-150 cursor-pointer px-3 lg:px-4 h-8 lg:h-10 ${!activeCategory
              ? "bg-sahi-primary-color text-white border-sahi-primary-color shadow-xs font-semibold"
              : "bg-white text-text-color filter-border hover:bg-gray-50 font-normal"
              }`}
          >
            <Globe className="h-4 w-4 shrink-0" />
            <span className="whitespace-nowrap">All Posts</span>
          </button>

          {/* Category Tabs */}
          {POST_CATEGORIES.map((cat) => {
            const isSelected = activeCategory === cat.id;
            const Icon = cat.icon;
            return (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(isSelected ? null : cat.id)}
                title={cat.label}
                className={`shrink-0 flex items-center gap-2 rounded-[5px] text-xs lg:text-sm border transition-all duration-150 cursor-pointer px-3 lg:px-4 h-8 lg:h-10 ${isSelected
                  ? "text-sahi-green border-sahi-green bg-[#0b5d1e15] shadow-xs font-semibold"
                  : "bg-white text-text-color filter-border hover:bg-gray-50 font-normal"
                  }`}
              >
                <Icon className={`h-4 w-4 shrink-0 ${isSelected ? "text-sahi-green" : "text-sub-text-color"}`} />
                <span className="whitespace-nowrap">{cat.label}</span>
              </button>
            );
          })}

          {/* Show All Network Toggle Checkbox */}
          <label
            className="flex items-center gap-2 cursor-pointer select-none shrink-0 ml-2"
            title={viewAll ? "Showing all posts" : "Showing posts from your network"}
          >
            <input
              type="checkbox"
              id="communityShowAllCheckbox"
              checked={viewAll}
              onChange={(e) => handleToggleViewAll(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 filter-border text-sahi-primary-color focus:ring-sahi-primary-color cursor-pointer accent-sahi-primary-color flex-shrink-0"
            />
            <span className="text-xs lg:text-sm font-medium text-text-color whitespace-nowrap">
              Show All
            </span>
          </label>
        </div>
      </div>

      {/* Main Layout: Feed on the left (1fr), Composer/Catalog on the right (1fr) matching reference */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 w-full max-w-[1300px] mx-auto items-start flex-1 min-h-0 lg:overflow-hidden">
        {/* Posts Feed Column - Scrollable independently on desktop */}
        <div className="w-full space-y-3.5 pb-20 sm:pb-4 min-w-0 lg:h-full lg:overflow-y-auto [scrollbar-width:thin] [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-zinc-300 hover:[&::-webkit-scrollbar-thumb]:bg-zinc-400 [&::-webkit-scrollbar-thumb]:rounded-full">
          {postsLoading ? (
            <>
              <PostCardSkeleton />
              <PostCardSkeleton />
              <PostCardSkeleton />
            </>
          ) : filteredPosts.length > 0 ? (
            filteredPosts.map((post) => (
              <PostCard
                key={post.PostID}
                post={post}
                currentUser={currentUser}
                onOpen={openPostDetail}
                onLikeToggle={handleLikeToggle}
                onDeletePost={requestDeletePost}
                onReportPost={handleReportPost}
                onAgencyClick={(agencyID, postObj) => handleAgencyClick(agencyID, postObj || post)}
                isPreviewing={previewAgency?.agencyID === post.CreatedAgencyID}
              />
            ))
          ) : (
            <div className="bg-sahi-white border border-sahi-border-color rounded-[7px] p-10 text-center space-y-3 shadow-xs">
              <div className="w-14 h-14 rounded-[7px] bg-sahi-disable flex items-center justify-center mx-auto text-sub-text-color">
                <Search className="h-6 w-6" />
              </div>
              <h3 className="text-base font-bold text-text-color">No posts found</h3>
              <p className="text-xs text-sub-text-color max-w-sm mx-auto">
                No community updates match your current filter or search criteria.
              </p>
              <button
                onClick={() => {
                  setActiveCategory(null);
                  setSearchQuery("");
                }}
                className="text-xs text-sahi-primary-color font-semibold underline hover:opacity-80 cursor-pointer"
              >
                Clear filters
              </button>
            </div>
          )}
        </div>

        {/* Right Column: Composer OR Compact Catalog Preview (Fixed in view) */}
        <div className="hidden lg:block min-w-0 w-full lg:h-full lg:overflow-y-auto custom-scroll rounded-[7px]">
          {previewAgency ? (
            <WebstorePreview
              agency={previewAgency}
              onClose={() => setPreviewAgency(null)}
              onOpenCreatePost={() => setPreviewAgency(null)}
            />
          ) : (
            <CommunitySidebar
              onPostCreated={handlePostCreated}
            />
          )}
        </div>
      </div>

      {/* Feed Entry Modal (Mobile only or fallback) */}
      <FeedEntry
        open={createPostOpen}
        onClose={() => setCreatePostOpen(false)}
        onPostCreated={handlePostCreated}
      />

      {/* Post Detail Drawer / Modal */}
      <PostDetailModal
        post={selectedPost}
        open={detailOpen}
        loading={detailLoading}
        onClose={() => {
          setDetailOpen(false);
          setFocusCommentInput(false);
          if (searchParams.has("postID") || searchParams.has("p")) {
            const nextParams = new URLSearchParams(searchParams);
            nextParams.delete("postID");
            nextParams.delete("p");
            setSearchParams(nextParams, { replace: true });
          }
        }}
        onAddReply={handleAddReply}
        onLikeToggle={handleLikeToggle}
        onDeletePost={requestDeletePost}
        onReportPost={handleReportPost}
        currentUser={currentUser}
        onAgencyClick={handleAgencyClick}
        focusCommentInput={focusCommentInput}
        initialImageIndex={selectedImageIndex}
      />

      {/* Delete Post Confirmation */}
      <AlertDialog
        open={deleteAlertOpen}
        onOpenChange={(open) => {
          setDeleteAlertOpen(open);
          if (!open) setDeleteTarget(null);
        }}
      >
        <AlertDialogContent className="w-[92%] sm:w-full max-w-md rounded-2xl p-5 sm:p-4 bg-white gap-4 border border-zinc-200/80 shadow-2xl">
          <AlertDialogHeader className="text-left gap-1.5">
            <AlertDialogTitle className="text-base sm:text-lg font-semibold text-zinc-900">
              Delete Post
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-zinc-600 leading-normal">
              Are you sure you want to delete this post? This will permanently remove the post from the community feed and cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="!flex !flex-row sm:!justify-start gap-2.5 pt-1">
            <Button
              onClick={handleDeletePost}
              disabled={deleting}
              className="sahi-primary-button flex-1 sm:flex-none sm:min-w-[120px] h-10"
            >
              {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
              {deleting ? "Deleting..." : "Delete Post"}
            </Button>
            <Button
              variant="outline"
              onClick={() => { setDeleteAlertOpen(false); setDeleteTarget(null); }}
              disabled={deleting}
              className="sahi-secondary-button flex-1 sm:flex-none sm:min-w-[100px] h-10"
            >
              <X className="w-3.5 h-3.5" /> Cancel
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Mobile Select View Drawer */}
      <Drawer open={filterDrawerOpen} onOpenChange={setFilterDrawerOpen}>
        <DrawerContent className="p-0 [&>div:first-child]:hidden top-auto !h-auto rounded-t-[22px] max-h-[85dvh] border-t border-zinc-100 z-[10002]">
          <div className="flex flex-col bg-zinc-50 rounded-t-[22px] select-none pb-5">
            {/* Header */}
            <div className="flex items-center justify-between px-4 h-16 shrink-0 rounded-t-[22px]">
              <div className="flex gap-4 items-center">
                <button
                  type="button"
                  tabIndex={-1}
                  className="p-0 w-auto h-auto flex-shrink-0 flex justify-center text-gray-500 cursor-pointer"
                  onClick={() => setFilterDrawerOpen(false)}
                >
                  <ArrowLeft className="h-[24px] w-[24px]" />
                </button>
                <p className="text-lg font-semibold text-gray-900">Filter Posts</p>
              </div>
            </div>

            {/* Content list */}
            <div className="overflow-y-auto custom-scroll flex-1 px-4 pt-0 pb-1 space-y-2.5">
              {/* All Posts Option */}
              <button
                type="button"
                onClick={() => {
                  setActiveCategory(null);
                  setFilterDrawerOpen(false);
                }}
                className="w-full flex items-center gap-3.5 p-3.5 bg-white border border-zinc-200 rounded-[12px] hover:bg-zinc-50 transition-all text-left cursor-pointer group active:bg-zinc-100 shadow-2xs"
              >
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 text-white transition-all group-active:scale-95 ${!activeCategory ? "bg-[#0B5D1E]" : "bg-[#15304a]"}`}>
                  <Globe className="w-5 h-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <h4 className="text-sm font-semibold text-gray-900 transition-colors">
                    All Posts
                  </h4>
                  <p className="text-xs text-gray-500 mt-0.5 truncate">
                    Browse all community posts
                  </p>
                </div>
                {!activeCategory && (
                  <CheckCircle className="w-5 h-5 text-sahi-green shrink-0" />
                )}
              </button>

              {/* 4 Category Options */}
              {POST_CATEGORIES.map((cat) => {
                const isSelected = activeCategory === cat.id;
                const Icon = cat.icon;
                const categoryDescMap = {
                  A: "Company profiles & intros",
                  B: "Offers, deals & discounts",
                  C: "Sourcing requests & RFQs",
                  E: "Headlines & announcements"
                };
                return (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => {
                      setActiveCategory(cat.id);
                      setFilterDrawerOpen(false);
                    }}
                    className="w-full flex items-center gap-3.5 p-3.5 bg-white border border-zinc-200 rounded-[12px] hover:bg-zinc-50 transition-all text-left cursor-pointer group active:bg-zinc-100 shadow-2xs"
                  >
                    <div
                      className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 text-white transition-all group-active:scale-95 ${isSelected ? "bg-[#0B5D1E]" : "bg-[#15304a]"}`}
                    >
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h4 className="text-sm font-semibold text-gray-900 transition-colors">
                        {cat.label}
                      </h4>
                      <p className="text-xs text-gray-500 mt-0.5 truncate">
                        {categoryDescMap[cat.id] || cat.label}
                      </p>
                    </div>
                    {isSelected && (
                      <CheckCircle className="w-5 h-5 text-sahi-green shrink-0" />
                    )}
                  </button>
                );
              })}

              {/* Network Filter Toggle - Distinct Section */}
              <div className="pt-2">
                <div
                  onClick={() => {
                    handleToggleViewAll(!viewAll);
                    setFilterDrawerOpen(false);
                  }}
                  className="w-full flex items-center p-1 justify-between cursor-pointer select-none"
                >
                  <div className="min-w-0 pr-4">
                    <h4 className="text-sm font-semibold text-gray-900">
                      Show only my network posts
                    </h4>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Filter posts from your business network
                    </p>
                  </div>
                  <Switch
                    checked={!viewAll}
                    onCheckedChange={(checked) => {
                      handleToggleViewAll(!checked);
                      setFilterDrawerOpen(false);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    className="cursor-pointer shrink-0"
                  />
                </div>
              </div>
            </div>
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
}
